import PropTypes from "prop-types";
import React, { useContext, useState } from "react";

import styled from "styled-components/macro";

import { useSaveDefaultFilters } from "Boards/APIReactQuery/queries";
import { BoardContext } from "Boards/BoardContext";
import { BOARD_TYPES } from "Boards/const";
import { motion } from "framer-motion";

import FnrButton3 from "UI/FnrButton3";
import { FnrPopover } from "UI/FnrPopover2";
import FnrSlides from "UI/FnrSlides";

import { MUTATION_TYPE } from "./SaveDefaultFilter";

SaveDefaultPopover.propTypes = {
  setShowPopover: PropTypes.func.isRequired,
  buttonRef: PropTypes.object.isRequired,
  activeElement: PropTypes.string,
  setShowTooltip: PropTypes.func.isRequired,
};

export default function SaveDefaultPopover({
  setShowPopover,
  buttonRef,
  activeElement,
  setShowTooltip,
}) {
  const [state, dispatch] = useContext(BoardContext);
  const boardID = state?.boardID;
  const boardType = state?.boardType;
  const groupID = state?.groupID;

  const [mutationType, setMutationType] = useState(null);

  // We changed "boards" to "reports" in the tooltip text, regressionless change here
  const boardNoun = boardType === BOARD_TYPES.REPORT ? "report" : "report";
  const tooltipContent = `Save the current filters as the default view for this ${boardNoun}`;

  const { mutate, isLoading, isError, isSuccess, reset } =
    useSaveDefaultFilters({
      boardID: boardID,
      onSuccess: () => {
        let filters =
          window.fnReactBus.filtersPanel.filterBuilder.buildRequest(false);
        dispatch({ type: "Update savedBoardFilter", payload: { filters } });

        setTimeout(() => {
          // uses timeout so that it can show the success checkmark before closing
          setMutationType(null);
          setShowPopover(false);
          setShowTooltip(false);
        }, 1000);

        setTimeout(() => {
          // prevents the success checkmark from showing on the next popover open
          reset();
        }, 1100);
      },
      onError: () => {
        setMutationType(null);
      },
    });

  const cancelSave = () => {
    setShowPopover(false);
    reset();
  };

  const saveFilters = () => {
    setMutationType(MUTATION_TYPE.SAVE);
    let filters =
      window.fnReactBus.filtersPanel.filterBuilder.buildRequest(false);
    mutate({ boardID, groupID, filters });
  };
  return (
    <FnrPopover
      show={Boolean(activeElement)}
      onShouldClose={cancelSave}
      buttonRef={buttonRef}
      offsetLeft={-100}
      style={{ width: "300px" }}
    >
      <FnrSlides
        clip={false}
        activeKey={activeElement}
        fullWidth
        components={[
          {
            key: "tooltip",
            value: () => <TooltipContent>{tooltipContent}</TooltipContent>,
          },
          {
            key: "popover",
            value: () => {
              return (
                <PopoverContent>
                  <P>
                    This will overwrite your existing default filters for this{" "}
                    {boardNoun}. Would you like to continue?
                  </P>

                  <SpaceBetween>
                    <FnrButton3
                      onClick={cancelSave}
                      loading={
                        isLoading && mutationType === MUTATION_TYPE.CLEAR
                      }
                    >
                      Cancel
                    </FnrButton3>
                    <FnrButton3
                      onClick={saveFilters}
                      color="green"
                      loading={isLoading && mutationType === MUTATION_TYPE.SAVE}
                      disabled={isLoading || isSuccess}
                      style={{ width: "75px" }}
                    >
                      {isSuccess ? <i className="fas fa-check" /> : "Save"}
                    </FnrButton3>
                  </SpaceBetween>

                  <Error isError={isError} />
                </PopoverContent>
              );
            },
          },
        ]}
      />
    </FnrPopover>
  );
}

const Error = ({ isError }) => {
  if (!isError) return null;

  return (
    // <AnimatePresence>
    //   {isError && (
    <MotionDiv
      key="error"
      // initial={{ height: 0, opacity: 0, scale: 0.8 }}
      // animate={{ height: "auto", opacity: 1, scale: 1 }}
    >
      <i className="fad fa-exclamation-triangle" />
      <div>Hmm. Something went wrong. Please try again.</div>
    </MotionDiv>
    //   )}
    // </AnimatePresence>
  );
};

const PopoverContent = styled.div`
  display: grid;
  gap: 1rem;
  padding: 0.5rem;
`;

const TooltipContent = styled.div`
  text-align: center;
`;

const MotionDiv = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f7f0b6;
  color: #d5a223;
  padding: 0.25rem;
  border-radius: 8px;
`;

const P = styled.p`
  margin: 0;
`;

const SpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
`;
